import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function GuidePage() {
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'OOPBuy Spreadsheet – Der komplette Guide für deutsche Nutzer 2026',
    description: 'Erfahre alles über das OOPBuy Spreadsheet: Wie es funktioniert, welche Kategorien es gibt, Sicherheitstipps und warum es die beste Wahl für dein China Shopping aus Deutschland ist.',
    author: {
      '@type': 'Organization',
      name: 'OOPBuy Spreadsheet',
    },
    datePublished: '2026-07-14',
    dateModified: '2026-07-14',
    publisher: {
      '@type': 'Organization',
      name: 'OOPBuy Spreadsheet',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Startseite',
        item: 'https://oopbuypread.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'OOPBuy Spreadsheet Guide',
        item: 'https://oopbuypread.com/oopbuy-spreadsheet-guide',
      },
    ],
  };

  return (
    <>
      <Navbar />

      <main>
        {/* Article Hero */}
        <section className="relative w-full min-h-[500px] md:min-h-[550px] flex items-center">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20minimalist%20desk%20workspace%20with%20spreadsheet%20on%20screen%2C%20notebook%20and%20coffee%2C%20soft%20natural%20morning%20light%2C%20Scandinavian%20aesthetic%2C%20clean%20composition%2C%20neutral%20beige%20tones%2C%20editorial%20product%20photography%2C%20calm%20focused%20atmosphere&width=1920&height=1080&seq=guide-hero-bg&orientation=landscape"
              alt="OOPBuy Spreadsheet Guide"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/35"></div>
          </div>

          <div className="relative z-10 w-full px-4 md:px-6 pt-24">
            <div className="max-w-4xl mx-auto">
              <nav className="flex items-center gap-2 text-sm text-background-100/70 mb-6">
                <Link to="/" className="hover:text-background-50 transition-colors cursor-pointer">
                  Startseite
                </Link>
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-s-line text-xs"></i>
                </span>
                <span className="text-background-50">OOPBuy Spreadsheet Guide</span>
              </nav>

              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-background-50 leading-tight tracking-tight">
                OOPBuy Spreadsheet
                <br />
                <span className="text-primary-300">Der komplette Guide</span>
                <br />
                <span className="text-background-50">für deutsche Nutzer 2026</span>
              </h1>

              <div className="mt-6 flex flex-wrap items-center gap-6 text-sm text-background-100/80">
                <span className="flex items-center gap-1.5">
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-calendar-line"></i>
                  </span>
                  14. Juli 2026
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-time-line"></i>
                  </span>
                  Lesezeit: 8 Minuten
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-file-text-line"></i>
                  </span>
                  ~2.200 Wörter
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <article className="py-16 md:py-24 px-4 md:px-6 bg-background-50">
          <div className="max-w-3xl mx-auto">
            {/* Introduction */}
            <div>
              <p className="text-lg text-foreground-700 leading-relaxed">
                Das <strong>OOPBuy Spreadsheet</strong> hat sich in den letzten Monaten zu einem der wichtigsten Werkzeuge für deutsche Online-Shopper entwickelt, die Produkte aus China beziehen möchten. In diesem umfassenden Guide erfährst du alles, was du über das OOPBuy Spreadsheet wissen musst – von den Grundlagen über die verschiedenen Kategorien bis hin zu wichtigen Sicherheitstipps.
              </p>

              <div className="mt-10 bg-background-100 rounded-2xl p-6 md:p-8">
                <h3 className="text-sm font-semibold text-foreground-500 uppercase tracking-wider mb-4">Inhaltsverzeichnis</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {[
                    'Was ist das OOPBuy Spreadsheet?',
                    'Die wichtigsten Funktionen im Überblick',
                    'Alle Produktkategorien detailliert erklärt',
                    'So nutzt du das OOPBuy Spreadsheet effizient',
                    'Sicherheit und Datenschutz beim OOPBuy Shopping',
                    'OOPBuy Spreadsheet vs. Alternativen',
                    'Tipps für Anfänger',
                    'Häufige Fragen und Antworten',
                  ].map((item, idx) => (
                    <a
                      key={idx}
                      href={`#section-${idx + 1}`}
                      className="flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer"
                    >
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-arrow-right-s-line text-primary-500"></i>
                      </span>
                      {item}
                    </a>
                  ))}
                </div>
              </div>

              {/* Section 1 */}
              <section id="section-1" className="mt-14">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">
                  Was ist das OOPBuy Spreadsheet?
                </h2>
                <p className="mt-4 text-base text-foreground-600 leading-relaxed">
                  Das <strong>OOPBuy Spreadsheet</strong> ist eine strukturierte, browserbasierte Produktdatenbank, die speziell für den deutschsprachigen Markt entwickelt wurde. Es vereint hunderte Produkte aus verschiedenen Kategorien in einer übersichtlichen, einfach zu bedienenden Oberfläche. Anders als bei einer manuellen Suche auf verschiedenen Plattformen bietet das OOPBuy Spreadsheet eine kuratierte Auswahl der besten und beliebtesten Produkte – sortiert, kategorisiert und mit allen wichtigen Informationen auf einen Blick.
                </p>
                <p className="mt-4 text-base text-foreground-600 leading-relaxed">
                  Die Idee hinter dem OOPBuy Spreadsheet ist einfach: Deutschen Nutzern den Zugang zu Produkten aus China zu erleichtern, ohne dass sie sich durch unzählige Webseiten, verwirrende Produktlisten oder unübersichtliche Suchergebnisse kämpfen müssen. Stattdessen findest du alle relevanten Produkte zentral an einem Ort – mit aktuellen Preisen, Bewertungen und direkten Verlinkungen zu den jeweiligen Angeboten.
                </p>
                <p className="mt-4 text-base text-foreground-600 leading-relaxed">
                  Das OOPBuy Spreadsheet ist <strong>kein Onlineshop</strong> im klassischen Sinne und verkauft selbst keine Produkte. Es ist vielmehr eine intelligente Informationsplattform, die dir hilft, bessere und schnellere Kaufentscheidungen zu treffen. Du kannst es dir wie einen persönlichen Shopping-Assistenten vorstellen, der die Arbeit der Produktrecherche für dich übernimmt.
                </p>
              </section>

              {/* Section 2 */}
              <section id="section-2" className="mt-14">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">
                  Die wichtigsten Funktionen im Überblick
                </h2>
                <p className="mt-4 text-base text-foreground-600 leading-relaxed">
                  Das OOPBuy Spreadsheet bietet eine Reihe von Funktionen, die es von einfachen Produktlisten oder manuellen Suchvorgängen deutlich abheben. Hier sind die Kernfunktionen, die das Spreadsheet so wertvoll machen:
                </p>

                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
                  {[
                    { title: 'Übersichtliche Kategorien', desc: '14 sorgfältig kuratierte Produktkategorien, von Shoes über Fashion bis zu Accessoires, ermöglichen eine schnelle Navigation.' },
                    { title: 'Preisvergleich', desc: 'Vergleiche Preise verschiedener Anbieter direkt im Spreadsheet und finde das beste Angebot für dein Budget.' },
                    { title: 'Aktuelle Daten', desc: 'Das Spreadsheet wird regelmäßig aktualisiert, sodass du stets Zugriff auf aktuelle Preise und Verfügbarkeiten hast.' },
                    { title: 'Deutsche Oberfläche', desc: 'Alle Beschreibungen, Kategorien und Hilfetexte sind vollständig auf Deutsch – optimiert für Nutzer aus Deutschland, Österreich und der Schweiz.' },
                  ].map((item) => (
                    <div key={item.title} className="bg-background-100 rounded-xl p-5">
                      <h3 className="text-sm font-semibold text-foreground-900">{item.title}</h3>
                      <p className="mt-2 text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* Section 3 */}
              <section id="section-3" className="mt-14">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">
                  Alle Produktkategorien detailliert erklärt
                </h2>
                <p className="mt-4 text-base text-foreground-600 leading-relaxed">
                  Das OOPBuy Spreadsheet deckt eine beeindruckende Bandbreite an Produktkategorien ab. Jede Kategorie wurde sorgfältig zusammengestellt, um die relevantesten und beliebtesten Produkte für deutsche Käufer zu präsentieren.
                </p>

                <div className="mt-6 overflow-x-auto">
                  <table className="w-full text-left border-collapse text-sm">
                    <thead>
                      <tr className="border-b-2 border-background-300">
                        <th className="py-3 px-4 text-xs font-semibold text-foreground-800">Kategorie</th>
                        <th className="py-3 px-4 text-xs font-semibold text-foreground-800">Beschreibung</th>
                        <th className="py-3 px-4 text-xs font-semibold text-foreground-800">Beliebtheit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { cat: 'Shoes', desc: 'Sneaker, Sportschuhe und elegante Schuhe aller Marken', pop: '★★★★★' },
                        { cat: 'Hoodies & Sweaters', desc: 'Gemütliche Pullover und Kapuzenjacken für jeden Stil', pop: '★★★★★' },
                        { cat: 'T-Shirts', desc: 'Basic-Shirts, Graphic-Tees und Premium-Baumwolle', pop: '★★★★☆' },
                        { cat: 'Jackets', desc: 'Jacken, Mäntel und Übergangsjacken für jede Jahreszeit', pop: '★★★★☆' },
                        { cat: 'Pants & Shorts', desc: 'Jeans, Hosen und Shorts in verschiedenen Passformen', pop: '★★★★☆' },
                        { cat: 'Headwear', desc: 'Caps, Mützen und Hüte als modisches Accessoire', pop: '★★★☆☆' },
                        { cat: 'Sets', desc: 'Komplette Outfit-Sets für einen abgestimmten Look', pop: '★★★☆☆' },
                        { cat: 'Unterwäsche', desc: 'Bequeme Basics und hochwertige Unterwäsche', pop: '★★★☆☆' },
                        { cat: 'Jersey', desc: 'Sporttrikots und Fanartikel beliebter Vereine', pop: '★★★★☆' },
                        { cat: 'Accessories', desc: 'Schmuck, Uhren, Sonnenbrillen und mehr', pop: '★★★★☆' },
                        { cat: 'Bags', desc: 'Rucksäcke, Handtaschen und Umhängetaschen', pop: '★★★★☆' },
                        { cat: 'Belts', desc: 'Ledergürtel und modische Gürtel für jeden Anlass', pop: '★★★☆☆' },
                        { cat: 'Perfumes', desc: 'Düfte und Parfums bekannter und neuer Marken', pop: '★★★☆☆' },
                        { cat: 'Other Stuff', desc: 'Sonstige interessante Produkte und Neuentdeckungen', pop: '★★★☆☆' },
                      ].map((row) => (
                        <tr key={row.cat} className="border-b border-background-200/70">
                          <td className="py-3 px-4 font-medium text-foreground-800">{row.cat}</td>
                          <td className="py-3 px-4 text-foreground-600">{row.desc}</td>
                          <td className="py-3 px-4 text-foreground-500">{row.pop}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              {/* Section 4 */}
              <section id="section-4" className="mt-14">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">
                  So nutzt du das OOPBuy Spreadsheet effizient
                </h2>
                <p className="mt-4 text-base text-foreground-600 leading-relaxed">
                  Die Nutzung des OOPBuy Spreadsheets ist denkbar einfach und erfordert keinerlei technische Vorkenntnisse. Hier ist eine Schritt-für-Schritt-Anleitung, die dich in wenigen Minuten zum Profi macht:
                </p>

                <div className="mt-6 space-y-6">
                  {[
                    { step: '1', title: 'Spreadsheet öffnen', content: 'Rufe das OOPBuy Spreadsheet über deinen Browser auf – egal ob am Desktop, Tablet oder Smartphone. Die Oberfläche passt sich automatisch an dein Gerät an.' },
                    { step: '2', title: 'Kategorie auswählen', content: 'Nutze die übersichtliche Kategorien-Navigation, um direkt zu den Produkten zu gelangen, die dich interessieren. Von Shoes bis Accessoires – alles ist nur einen Klick entfernt.' },
                    { step: '3', title: 'Produkte vergleichen', content: 'Sieh dir die aufgelisteten Produkte in deiner gewählten Kategorie an. Vergleiche Preise, Bewertungen und wichtige Details, bevor du eine Entscheidung triffst.' },
                    { step: '4', title: 'Zum Anbieter navigieren', content: 'Hast du das passende Produkt gefunden, gelangst du mit einem Klick direkt zur Angebotsseite des jeweiligen Anbieters. Von dort aus kannst du deinen Kauf abschließen.' },
                    { step: '5', title: 'Kauf abschließen', content: 'Auf der Anbieterseite prüfst du noch einmal alle Details und schließt den Kauf sicher ab. Achte dabei auf sichere Zahlungsmethoden und Käuferschutz.' },
                  ].map((item) => (
                    <div key={item.step} className="flex gap-4">
                      <span className="flex-shrink-0 w-10 h-10 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center text-sm font-bold">
                        {item.step}
                      </span>
                      <div>
                        <h3 className="text-base font-semibold text-foreground-900">{item.title}</h3>
                        <p className="mt-1 text-sm text-foreground-600 leading-relaxed">{item.content}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* Section 5 */}
              <section id="section-5" className="mt-14">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">
                  Sicherheit und Datenschutz beim OOPBuy Shopping
                </h2>
                <p className="mt-4 text-base text-foreground-600 leading-relaxed">
                  Ein wichtiges Thema für viele deutsche Nutzer ist die Sicherheit beim Online-Shopping – besonders wenn es um Plattformen geht, die Produkte aus China anbieten. Das OOPBuy Spreadsheet wurde mit genau diesem Sicherheitsgedanken entwickelt.
                </p>
                <p className="mt-4 text-base text-foreground-600 leading-relaxed">
                  Das Spreadsheet selbst ist eine <strong>reine Informationsquelle</strong>: Es sammelt keine persönlichen Daten, verwendet keine Tracking-Cookies und installiert keine Software auf deinem Gerät. Du kannst das OOPBuy Spreadsheet vollkommen anonym nutzen, ohne dass deine Daten erfasst oder weitergegeben werden.
                </p>
                <p className="mt-4 text-base text-foreground-600 leading-relaxed">
                  Dennoch gibt es einige grundlegende Sicherheitstipps, die du beim Shopping beachten solltest. Erstens: Kaufe nur bei verifizierten und gut bewerteten Anbietern. Das OOPBuy Spreadsheet listet bevorzugt Produkte von vertrauenswürdigen Quellen auf, aber ein kurzer Blick auf die Verkäuferbewertungen schadet nie. Zweitens: Verwende sichere Zahlungsmethoden wie PayPal oder Kreditkarte mit Käuferschutz – vermeide direkte Banküberweisungen an unbekannte Händler. Drittens: Lies die Produktbeschreibungen sorgfältig und achte auf Details wie Größenangaben, Materialien und Versandbedingungen.
                </p>

                <div className="mt-6 bg-accent-50 rounded-xl p-6 border border-accent-200/60">
                  <div className="flex items-start gap-3">
                    <span className="w-6 h-6 flex-shrink-0 flex items-center justify-center mt-0.5">
                      <i className="ri-shield-check-line text-accent-600 text-lg"></i>
                    </span>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900">Sicherheits-Checkliste</h3>
                      <ul className="mt-2 space-y-1.5">
                        {[
                          'Nur bei verifizierten Händlern mit guten Bewertungen kaufen',
                          'Sichere Zahlungsmethoden mit Käuferschutz verwenden',
                          'Produktbilder und -beschreibungen genau prüfen',
                          'Versandbedingungen und Lieferzeiten vor dem Kauf checken',
                          'Keine persönlichen Daten außerhalb der Bestellabwicklung teilen',
                        ].map((tip, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-foreground-600">
                            <span className="w-4 h-4 flex-shrink-0 flex items-center justify-center">
                              <i className="ri-check-line text-accent-500 text-xs"></i>
                            </span>
                            {tip}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </section>

              {/* Section 6 */}
              <section id="section-6" className="mt-14">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">
                  OOPBuy Spreadsheet vs. Alternativen
                </h2>
                <p className="mt-4 text-base text-foreground-600 leading-relaxed">
                  Natürlich gibt es auch andere Wege, um Produkte für das China Shopping zu finden. Aber wie schneidet das OOPBuy Spreadsheet im direkten Vergleich ab?
                </p>

                <div className="mt-6 overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="border-b-2 border-background-300">
                        <th className="py-3 px-4 text-xs font-semibold text-foreground-800">Merkmal</th>
                        <th className="py-3 px-4 text-xs font-semibold text-primary-600 bg-primary-50/50">OOPBuy Spreadsheet</th>
                        <th className="py-3 px-4 text-xs font-semibold text-foreground-800">Manuelle Suche</th>
                        <th className="py-3 px-4 text-xs font-semibold text-foreground-800">Social Media</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { feature: 'Übersichtlichkeit', oopbuy: '★★★★★', manual: '★★★☆☆', social: '★★☆☆☆' },
                        { feature: 'Aktualität', oopbuy: '★★★★★', manual: '★★★★☆', social: '★★★☆☆' },
                        { feature: 'Deutsche Sprache', oopbuy: '★★★★★', manual: '★★☆☆☆', social: '★★☆☆☆' },
                        { feature: 'Preisvergleich', oopbuy: '★★★★★', manual: '★★★☆☆', social: '★☆☆☆☆' },
                        { feature: 'Anfängerfreundlich', oopbuy: '★★★★★', manual: '★★★☆☆', social: '★★☆☆☆' },
                      ].map((row) => (
                        <tr key={row.feature} className="border-b border-background-200/70">
                          <td className="py-3 px-4 text-sm font-medium text-foreground-800">{row.feature}</td>
                          <td className="py-3 px-4 text-sm text-primary-600 bg-primary-50/30">{row.oopbuy}</td>
                          <td className="py-3 px-4 text-sm text-foreground-600">{row.manual}</td>
                          <td className="py-3 px-4 text-sm text-foreground-600">{row.social}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <p className="mt-6 text-base text-foreground-600 leading-relaxed">
                  Wie die Tabelle deutlich zeigt, bietet das <strong>OOPBuy Spreadsheet</strong> in praktisch allen relevanten Kategorien die beste Performance. Besonders die Kombination aus Übersichtlichkeit, deutscher Sprache und einfacher Bedienbarkeit macht es zur ersten Wahl für Einsteiger und erfahrene Shopper gleichermaßen.
                </p>
              </section>

              {/* Section 7 */}
              <section id="section-7" className="mt-14">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">
                  Tipps für Anfänger
                </h2>
                <p className="mt-4 text-base text-foreground-600 leading-relaxed">
                  Du bist neu im China Shopping und nutzt das OOPBuy Spreadsheet zum ersten Mal? Keine Sorge – mit diesen praktischen Tipps gelingt dir der Einstieg garantiert:
                </p>

                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { tip: 'Starte mit beliebten Kategorien', detail: 'Shoes und T-Shirts sind ideale Einstiegskategorien – hier findest du die größte Auswahl und die meisten Bewertungen.' },
                    { tip: 'Vergleiche mehrere Anbieter', detail: 'Schaue dir mindestens zwei bis drei Angebote für ähnliche Produkte an, bevor du dich entscheidest.' },
                    { tip: 'Achte auf Größenangaben', detail: 'Asiatische Größen fallen oft kleiner aus. Prüfe immer die Größentabellen der Anbieter und lies die Bewertungen anderer Käufer.' },
                    { tip: 'Nutze die Suchfunktion', detail: 'Wenn du etwas Bestimmtes suchst, kannst du innerhalb des Spreadsheets schnell navigieren und die passende Kategorie finden.' },
                  ].map((item, i) => (
                    <div key={i} className="bg-background-100 rounded-xl p-5">
                      <div className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                          <span className="text-xs font-bold text-primary-600">{i + 1}</span>
                        </span>
                        <div>
                          <h3 className="text-sm font-semibold text-foreground-900">{item.tip}</h3>
                          <p className="mt-1.5 text-xs text-foreground-500 leading-relaxed">{item.detail}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* Section 8 */}
              <section id="section-8" className="mt-14">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">
                  Häufige Fragen und Antworten
                </h2>

                <div className="mt-6 space-y-4">
                  {[
                    {
                      q: 'Muss ich mich registrieren, um das OOPBuy Spreadsheet zu nutzen?',
                      a: 'Nein, das OOPBuy Spreadsheet ist ohne Registrierung und völlig anonym nutzbar. Du kannst sofort loslegen.',
                    },
                    {
                      q: 'Funktioniert das OOPBuy Spreadsheet auch auf dem Smartphone?',
                      a: 'Ja, das OOPBuy Spreadsheet ist vollständig responsive und passt sich automatisch an Bildschirme aller Größen an – vom Desktop bis zum Smartphone.',
                    },
                    {
                      q: 'Werden die Produkte direkt über das Spreadsheet verkauft?',
                      a: 'Nein, das OOPBuy Spreadsheet ist eine reine Informationsplattform. Es listet Produkte auf und verlinkt zu den jeweiligen Anbietern, verkauft aber selbst keine Artikel.',
                    },
                    {
                      q: 'Wie verdient OOPBuy Spreadsheet Geld?',
                      a: 'Das OOPBuy Spreadsheet ist für Nutzer vollständig kostenlos. Es finanziert sich durch die Weiterleitung von Interessenten zu Partnershops.',
                    },
                  ].map((faq, i) => (
                    <div key={i} className="bg-background-100 rounded-xl p-5">
                      <h3 className="text-sm font-semibold text-foreground-900">{faq.q}</h3>
                      <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* CTA am Ende des Artikels */}
              <div className="mt-14 bg-primary-50 rounded-2xl p-8 text-center border border-primary-200/60">
                <h3 className="font-heading text-xl font-bold text-foreground-950">
                  Bereit, das OOPBuy Spreadsheet selbst auszuprobieren?
                </h3>
                <p className="mt-3 text-sm text-foreground-600 max-w-lg mx-auto">
                  Entdecke jetzt die komplette Produktvielfalt auf Kaspreadsheet und finde genau die Produkte, die du suchst.
                </p>
                <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="w-full sm:w-auto px-7 py-3 text-sm font-semibold bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    oopbuy spreadsheet
                    <i className="ri-arrow-right-up-line ml-2"></i>
                  </a>
                  <Link
                    to="/"
                    className="w-full sm:w-auto px-7 py-3 text-sm font-medium border-2 border-foreground-300 text-foreground-700 rounded-full hover:bg-foreground-950 hover:text-background-50 hover:border-foreground-950 transition-all duration-200 whitespace-nowrap cursor-pointer"
                  >
                    Zurück zur Startseite
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </article>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
    </>
  );
}